import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { isValidEmail } from '@lender/utils';
import { Button, ButtonGroup, Chip, ClickAwayListener, Divider, Typography } from '@mui/material';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import clsx from 'clsx';
import _ from 'lodash';
import PropType from 'prop-types';

import { selectUserData } from 'store/auth/selectors';
import {
  selectApplicationContactsWithBorrowers,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';

import { ActivitiesTestIds } from '../../../Activities.testIds';
import { MAX_RECIPIENTS_LENGTH } from '../../EmailCreationModal.config';
import { DUPLICATE_EMAIL_ERROR, INVALID_EMAIL_ERROR } from '../../EmailCreationModal.constants';

import {
  getOptionLabelSafe,
  isDuplicateEmail,
  normalizeFreeSoloValue,
} from './RecipientsFields.utils';

const RecipientsFields = ({
  isLoading,
  form,
  setInForm,
  validationErrors,
  validateField,
  preventSubmitHandler,
  shouldExpandCc = false,
  onRecipientFieldErrorsChange,
}) => {
  const contacts = useSelector(selectApplicationContactsWithBorrowers);
  const senderData = useSelector(selectUserData);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ACTIVITIES_EMAILS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ACTIVITIES_EMAILS));

  const isDisabledByLock = isLoading || isPageLocked;

  const [isEdit, setEdit] = useState(false);

  const toggleEdit = useCallback(() => setEdit(prevIsEdit => !prevIsEdit), []);

  const [isCCEnabled, setCCEnabled] = useState(shouldExpandCc);
  const [isBCCEnabled, setBCCEnabled] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [inputValues, setInputValues] = useState({ recipients: '', cc: '', bcc: '' });

  useEffect(() => {
    const hasErrors = !!(fieldErrors.recipients || fieldErrors.cc || fieldErrors.bcc);
    onRecipientFieldErrorsChange?.(hasErrors);
  }, [fieldErrors, onRecipientFieldErrorsChange]);

  const toggleCC = useCallback(() => setCCEnabled(prevIsCCEnabled => !prevIsCCEnabled), []);
  const toggleBCC = useCallback(() => setBCCEnabled(prevIsBCCEnabled => !prevIsBCCEnabled), []);

  const recipientsOptions = useMemo(() => {
    const contactsToExclude = _.concat(form.recipients, form.cc, form.bcc, {
      value: senderData.email,
    });

    return _.differenceBy(contacts, contactsToExclude, 'value');
  }, [contacts, form.bcc, form.cc, form.recipients, senderData.email]);

  const allRecipients = [...form.recipients, ...form.cc, ...form.bcc];
  const previewRecipients = allRecipients.length > 2 ? allRecipients.slice(0, 2) : allRecipients;
  const recipientsError =
    validationErrors.recipients || validationErrors.cc || validationErrors.bcc;

  const autocompleteInputProps = useMemo(
    () => ({
      onKeyDown: preventSubmitHandler,
    }),
    [preventSubmitHandler],
  );

  const getOptionDisabled = useCallback(
    fieldName => () => form[fieldName].length >= MAX_RECIPIENTS_LENGTH,
    [form],
  );

  const onClickAway = useCallback(() => {
    const hasUnsavedInput = inputValues.recipients || inputValues.cc || inputValues.bcc;
    const hasErrors = fieldErrors.recipients || fieldErrors.cc || fieldErrors.bcc;

    if (hasUnsavedInput || hasErrors) {
      return;
    }

    toggleEdit();
    setCCEnabled(!!form.cc.length);
    setBCCEnabled(!!form.bcc.length);
  }, [fieldErrors, form.bcc.length, form.cc.length, inputValues, toggleEdit]);

  const onChangeBase = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const createInputChangeHandler = useCallback(
    fieldName => (e, newInputValue, reason) => {
      if (reason !== 'input') {
        return;
      }

      setFieldErrors(prev => (prev[fieldName] ? { ...prev, [fieldName]: null } : prev));
      setInputValues(prev => ({ ...prev, [fieldName]: newInputValue }));
    },
    [],
  );

  const createFieldChangeHandler = useCallback(
    fieldName => (e, rawValue) => {
      const normalized = normalizeFreeSoloValue(rawValue);
      const previousValues = form[fieldName];

      const newEntries = normalized.filter(
        item => !previousValues.some(prev => prev.value === item.value),
      );

      const allCurrentRecipients = [...form.recipients, ...form.cc, ...form.bcc];

      const invalidEntry = newEntries.find(
        entry => _.isNil(entry.description) && !isValidEmail(entry.value),
      );

      if (invalidEntry) {
        setFieldErrors(prev => ({ ...prev, [fieldName]: INVALID_EMAIL_ERROR }));

        return;
      }

      const duplicateEntry = newEntries.find(entry =>
        isDuplicateEmail(entry.value, allCurrentRecipients),
      );

      if (duplicateEntry) {
        setFieldErrors(prev => ({ ...prev, [fieldName]: DUPLICATE_EMAIL_ERROR }));

        return;
      }

      setFieldErrors(prev => ({ ...prev, [fieldName]: null }));
      setInputValues(prev => ({ ...prev, [fieldName]: '' }));
      onChangeBase(e, normalized);
    },
    [form, onChangeBase],
  );

  const onInputChangeRecipients = useMemo(
    () => createInputChangeHandler('recipients'),
    [createInputChangeHandler],
  );
  const onInputChangeCc = useMemo(() => createInputChangeHandler('cc'), [createInputChangeHandler]);
  const onInputChangeBcc = useMemo(
    () => createInputChangeHandler('bcc'),
    [createInputChangeHandler],
  );

  const onChangeRecipients = useMemo(
    () => createFieldChangeHandler('recipients'),
    [createFieldChangeHandler],
  );
  const onChangeCc = useMemo(() => createFieldChangeHandler('cc'), [createFieldChangeHandler]);
  const onChangeBcc = useMemo(() => createFieldChangeHandler('bcc'), [createFieldChangeHandler]);

  const renderOptionContent = useCallback(
    ({ option, optionText }) => (
      <>
        <span>{optionText}</span>
        {option.description && (
          <Typography noWrap color="textSecondary" variant="regular14">
            {option.description}
          </Typography>
        )}
      </>
    ),
    [],
  );

  return isEdit ? (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className="space-y-3">
        <div className="flex items-center min-h-32">
          <Typography className="mr-12" variant="regular14">
            To:
          </Typography>
          <AutocompleteCDS
            autoFocus
            autoSelect
            filterSelectedOptions
            freeSolo
            multiple
            OptionContent={renderOptionContent}
            dataTestId={ActivitiesTestIds.RECIPIENTS_TO_SELECT}
            dataTestIdInput={ActivitiesTestIds.RECIPIENTS_INPUT}
            disabled={isDisabledByLock || lockedFieldsMap.recipients}
            error={!!fieldErrors.recipients}
            getOptionDisabled={getOptionDisabled('recipients')}
            getOptionLabel={getOptionLabelSafe}
            helperText={fieldErrors.recipients}
            inputProps={autocompleteInputProps}
            inputValue={inputValues.recipients}
            name="recipients"
            optionClassName="flex justify-between w-full"
            optionName="label"
            options={recipientsOptions}
            value={form.recipients}
            width="100%"
            onChange={onChangeRecipients}
            onInputChange={onInputChangeRecipients}
          />

          <ButtonGroup size="small" variant="text">
            {!isCCEnabled && (
              <Button
                data-testid={ActivitiesTestIds.RECIPIENTS_CC_TOGGLE_BUTTON}
                value="cc"
                onClick={toggleCC}
              >
                CC
              </Button>
            )}

            {!isBCCEnabled && (
              <Button
                data-testid={ActivitiesTestIds.RECIPIENTS_BCC_TOGGLE_BUTTON}
                value="bcc"
                onClick={toggleBCC}
              >
                BCC
              </Button>
            )}
          </ButtonGroup>
        </div>

        {isCCEnabled && (
          <>
            <Divider />

            <div className="flex items-center min-h-32">
              <Typography className="mr-12" variant="regular14">
                CC:
              </Typography>
              <AutocompleteCDS
                autoFocus
                autoSelect
                filterSelectedOptions
                freeSolo
                multiple
                OptionContent={renderOptionContent}
                dataTestId={ActivitiesTestIds.RECIPIENTS_CC_SELECT}
                dataTestIdInput={ActivitiesTestIds.RECIPIENTS_INPUT}
                disabled={isDisabledByLock || lockedFieldsMap.cc}
                error={!!fieldErrors.cc}
                getOptionDisabled={getOptionDisabled('cc')}
                getOptionLabel={getOptionLabelSafe}
                helperText={fieldErrors.cc}
                inputProps={autocompleteInputProps}
                inputValue={inputValues.cc}
                name="cc"
                optionClassName="flex justify-between w-full"
                optionName="label"
                options={recipientsOptions}
                value={form.cc}
                width="100%"
                onChange={onChangeCc}
                onInputChange={onInputChangeCc}
              />
            </div>
          </>
        )}

        {isBCCEnabled && (
          <>
            <Divider />

            <div className="flex items-center min-h-32">
              <Typography className="mr-12" variant="regular14">
                BCC:
              </Typography>
              <AutocompleteCDS
                autoFocus
                autoSelect
                filterSelectedOptions
                freeSolo
                multiple
                OptionContent={renderOptionContent}
                className="m-0 p-0"
                dataTestId={ActivitiesTestIds.RECIPIENTS_BCC_SELECT}
                dataTestIdInput={ActivitiesTestIds.RECIPIENTS_INPUT}
                disabled={isDisabledByLock || lockedFieldsMap.bcc}
                error={!!fieldErrors.bcc}
                getOptionDisabled={getOptionDisabled('bcc')}
                getOptionLabel={getOptionLabelSafe}
                helperText={fieldErrors.bcc}
                inputProps={autocompleteInputProps}
                inputValue={inputValues.bcc}
                name="bcc"
                optionClassName="flex justify-between w-full"
                optionName="label"
                options={recipientsOptions}
                value={form.bcc}
                width="100%"
                onChange={onChangeBcc}
                onInputChange={onInputChangeBcc}
              />
            </div>
          </>
        )}
      </div>
    </ClickAwayListener>
  ) : (
    <div
      className={clsx(recipientsError ? 'items-baseline' : 'items-center', 'flex min-h-32')}
      onClick={toggleEdit}
    >
      <Typography className="mr-12" variant="regular14">
        To:
      </Typography>
      <div className="flex flex-col">
        <div className="flex items-center space-x-12">
          {previewRecipients.map(option => (
            <Chip key={option.value} label={option.label} />
          ))}
          {previewRecipients.length !== allRecipients.length && (
            <Typography variant="regular14">
              +{allRecipients.length - previewRecipients.length} more
            </Typography>
          )}
        </div>

        {recipientsError && (
          <Typography className="mt-5" color="error" variant="regular12">
            {recipientsError}
          </Typography>
        )}
      </div>
    </div>
  );
};

RecipientsFields.propTypes = {
  isLoading: PropType.bool.isRequired,
  form: PropType.object.isRequired,
  setInForm: PropType.func.isRequired,
  validationErrors: PropType.object.isRequired,
  validateField: PropType.func.isRequired,
  preventSubmitHandler: PropType.func.isRequired,
  shouldExpandCc: PropType.bool,
  onRecipientFieldErrorsChange: PropType.func,
};

export default memo(RecipientsFields);
